import type { ComponentsConfig } from '@/components/types/ComponentsConfig';
import appIcons from '@/constants/appIcons';
import assetsPaths from '@/constants/assetsPaths';
import { regexPatterns } from '@/constants/regexPatterns';

export const componentsConfig: ComponentsConfig = {
    accordion: {
        titleSize: 'sm',
        contentSize: 'xs',
        titleColor: 'primary',
        contentColor: 'secondary',
        titleHoverColor: 'accent',
        weight: 'semibold',
        width: 'full',
    },
    avatar: {
        placeholder: assetsPaths.placeholders.avatar,
        radius: 'full',
        size: 12,
        chipSize: 3,
        color: 'accent',
    },
    alert: {
        titleTextSize: 'md',
        messageTextSize: 'sm',
        variant: 'soft',
        color: 'accent',
        radius: 'xl',
        shadow: 'sm',
    },
    badge: {
        variant: 'soft',
        color: 'accent',
        size: 'xs',
        weight: 'medium',
        horizontalPadding: 'xl',
        verticalPadding: 'md',
        justify: 'center',
        radius: 'xl',
        width: 'auto',
    },
    brand: {
        size: 'lg',
    },
    breadcrumb: {
        color: 'secondary',
        activeColor: 'primary',
        separatorIcon: appIcons.outline.rightArrow,
        separatorColor: 'quaternary',
        hasHome: false,
        homeIcon: appIcons.outline.home,
        homeLink: '/',
    },
    button: {
        type: 'button',
        variant: 'solid',
        color: 'accent',
        hoverColor: 'accent',
        size: 'sm',
        weight: 'medium',
        horizontalPadding: '2xl',
        verticalPadding: 'md',
        justify: 'center',
        radius: 'lg',
        width: 'auto',
    },
    card: {
        titleSize: 'lg',
        radius: 'xl',
        shadow: 'sm',
        hasBorder: false,
        paddingSize: '3xl',
        prefixIconSize: 4,
    },
    checkbox: {
        size: 4,
        iconSize: 2,
        color: 'accent',
        radius: 'sm',
        labelSize: 'sm',
        labelColor: 'primary',
        footnoteSize: 'xs',
        footnoteColor: 'tertiary',
    },
    checkboxGroup: {
        direction: 'vertical',
        gap: 3,
    },
    collapsible: {
        size: 'sm',
        weight: 'medium',
        color: 'primary',
        hoverColor: 'accent',
        icon: appIcons.outline.downArrow,
        width: 'full',
    },
    colorBox: {
        radius: 'full',
        width: 8,
        height: 4,
        textSize: '2xs',
    },
    colorPicker: {
        colorRadius: 'md',
        iconClassName: 'size-3.5',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
        pattern: regexPatterns.colorPicker,
    },
    confirmationDialog: {
        primaryButtonColor: 'accent',
        secondaryButtonColor: 'accent',
        primaryButtonHorizontalPadding: '5xl',
        secondaryButtonHorizontalPadding: '5xl',
        primaryButtonVariant: 'solid',
        secondaryButtonVariant: 'ghost',
        messageColor: 'secondary',
        messageTextSize: 'sm',
        buttonsGap: 2,
        buttonsJustify: 'end',
    },
    datePicker: {
        numberOfMonths: 1,
        placeholder: 'Select Date',
        dateFormat: 'D MMM YYYY',
        dateTimeFormat: 'D MMM YYYY - hh:mm a',
        suffixIcon: appIcons.outline.calendar,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
        dismissOnSelect: true,
        isClearable: true,
    },
    timePicker: {
        timeFormat: 'hh:mm a',
        suffixIcon: appIcons.outline.clock,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'sm',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
    },
    tooltip: {
        delay: 200,
        placement: 'top',
        sideOffset: 5,
        radius: 'xl',
        horizontalPadding: 'md',
        verticalPadding: 'md',
        justify: 'center',
        background: 'primary',
    },
    divider: {
        orientation: 'horizontal',
        thickness: 'xs',
        color: 'tertiary',
    },
    drawer: {
        direction: 'end',
        titleSize: 'md',
        iconColor: 'accent',
        hasCloseButton: true,
    },
    emptyPlaceholder: {
        containerPaddingSize: '4xl',
        titleSize: 'xl',
        messageSize: 'xs',
    },
    dropzone: {
        borderColor: 'accent',
        maxSize: '10MB',
        allowedFileTypes: ['csv', 'xlsx', 'xls'],
        width: 'full',
        alignment: 'center',
        showPreview: true,
        allowMultiple: true,
    },
    fileUploader: {
        folder: 'attachments',
        iconClassName: 'size-3.5',
        radius: 'lg',
        width: 'full',
        textSize: 'sm',
        paddingSize: 'lg',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        border: 'transparent',
        hasBorder: false,
    },
    hStack: {
        justify: 'center',
        alignment: 'center',
        width: 'full',
    },
    icon: {
        color: 'none',
        size: 4,
    },
    iconBox: {
        color: 'accent',
        boxSize: 12,
        iconSize: 6,
        radius: 'xl',
    },
    image: {
        placeholder: assetsPaths.placeholders.image,
        radius: 'none',
    },
    inputAffix: {
        direction: 'start',
        radius: 'lg',
        background: 'secondary',
        invalidBackground: 'transparent',
        color: 'primary',
        border: 'transparent',
    },
    infoBox: {
        textSize: 'sm',
        padding: 'none',
    },
    link: {
        target: '_self',
        variant: 'link',
        color: 'primary',
        hoverColor: 'accent',
        size: 'sm',
        weight: 'medium',
        horizontalPadding: '2xl',
        verticalPadding: 'lg',
        justify: 'center',
        radius: 'lg',
        width: 'auto',
        isDimmed: true,
    },
    loader: {
        containerPaddingSize: '6xl',
        titleSize: 'xl',
        messageSize: 'xs',
        variant: 'default',
        radius: 'xl',
        shadow: 'sm',
    },
    maskInput: {
        pattern: regexPatterns.maskInput,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
    },
    menu: {
        icon: appIcons.outline.dots,
        variant: 'soft',
        color: 'secondary',
        menuColor: 'accent',
        size: 12,
        iconSize: 4,
        radius: 'xl',
        horizontalPadding: 'md',
        verticalPadding: 'md',
        outerRadius: 'xl',
        innerRadius: 'lg',
        hasBorder: true,
        menuOffset: 8,
    },
    dialog: {
        hasCloseButton: true,
        dismissOnClickOutside: true,
        titleSize: 'sm',
    },
    navigationTabs: {
        gap: 2,
        itemBackgroundColor: 'none',
        outerRadius: 'xl',
        innerRadius: 'lg',
        backgroundColor: 'none',
        textSize: 'sm',
        weight: 'normal',
    },
    numberInput: {
        min: 0,
        max: 100000,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
        allowDecimals: true,
        decimalScale: 2,
        border: 'transparent',
    },
    otpInput: {
        numInputs: 6,
        inputType: 'tel',
        isAutoFocus: true,
        skipDefaultStyles: true,
        radius: 'lg',
        width: 'full',
        shadow: 'none',
        background: 'secondary',
        border: 'transparent',
    },
    progressBar: {
        height: 2,
        textSize: 'xs',
        radius: 'md',
        color: 'accent',
        shadow: 'sm',
        showValue: false,
        variant: 'soft',
    },
    progressCircle: {
        size: 9,
        textSize: 'sm',
        radius: 'full',
        color: 'accent',
    },
    select: {
        menuPlacement: 'auto',
        dismissOnChange: true,
        color: 'accent',
        isLoading: false,
        isClearable: true,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        verticalPadding: 'xl',
        horizontalPadding: 'none',
        textSize: 'sm',
        shadow: 'none',
        background: 'secondary',
        hasBorder: false,
        maxDisplayedItems: 3,
    },
    spinner: {
        size: 6,
        color: 'accent',
    },
    stack: {
        justify: 'start',
        alignment: 'start',
        width: 'full',
    },
    switcher: {
        size: 'md',
        color: 'accent',
    },
    table: {
        hasBorder: true,
        radius: 'xl',
        shadow: 'sm',
    },
    tableData: {
        primaryTextSize: 'xs',
        primaryTextColor: 'tertiary',
        primaryTextWeight: 'normal',
        secondaryTextSize: '2xs',
        secondaryTextColor: 'tertiary',
        secondaryTextWeight: 'normal',
        textAlignment: 'start',
        contentAlignment: 'start',
        weight: 'normal',
    },
    tableHead: {
        actionColumnSize: 'xs',
        actionColumnAlignment: 'center',
        actionColumnWeight: 'medium',
        actionColumnColor: 'secondary',
    },
    tableRow: {
        backgroundColor: 'default',
        hasBorder: true,
    },
    tabs: {
        optionIdKey: 'id',
        optionTitleKey: 'title',
        optionDisabledKey: 'isDisabled',
        radius: 'lg',
        color: 'accent',
        textSize: 'xs',
        textWeight: 'medium',
    },
    tagsInput: {
        separator: 'Enter',
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
    },
    text: {
        size: 'md',
        weight: 'medium',
        color: 'primary',
        alignment: 'start',
        width: 'auto',
    },
    textarea: {
        rows: 4,
        resize: 'none',
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        hasBorder: false,
    },
    textInput: {
        pattern: regexPatterns.text,
        iconClassName: 'size-3.5',
        validationType: 'onBlur',
        radius: 'lg',
        width: 'full',
        paddingSize: '3xl',
        verticalPadding: 'xl',
        horizontalPadding: 'sm',
        textSize: 'sm',
        shadow: 'none',
        color: 'primary',
        background: 'secondary',
        border: 'transparent',
    },
    vStack: {
        justify: 'start',
        alignment: 'start',
        width: 'full',
    },
    rangeSlider: {
        step: 1,
        trackColor: 'secondary',
        selectedColor: 'accent',
        thumbColor: 'white',
        thumbBorderColor: 'primary',
        orientation: 'horizontal',
        allowPearling: true,
    },
    slider: {
        step: 1,
        trackColor: 'secondary',
        selectedColor: 'accent',
        thumbColor: 'white',
        thumbBorderColor: 'primary',
        orientation: 'horizontal',
    },
    radioGroup: {
        direction: 'vertical',
        gap: 3,
        justify: 'start',
    },
    view: {
        width: 'full',
        height: 'auto',
        display: 'block',
    },
    label: {
        textSize: { base: 'xs', lg: 'sm' },
    },
    toast: {
        variant: 'solid',
        color: 'accent',
        radius: 'lg',
        titleSize: 'sm',
        messageSize: '2xs',
        titleColor: 'white',
        messageColor: 'white',
        titleWeight: 'medium',
        messageWeight: 'medium',
        autoClose: 500000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        draggable: true,
        pauseOnHover: true,
        newestOnTop: true,
        closeIcon: appIcons.outline.close,
        successIcon: appIcons.outline.tickCircle,
        errorIcon: appIcons.outline.closeSquare,
        warningIcon: appIcons.outline.warning,
        infoIcon: appIcons.outline.eye,
    },
};
